/**
 * Leitura do plano de contas canônico no frontend.
 *
 * O DADO não mora aqui: mora em `planoCanonico.gerado.js`, espelho derivado de
 * `dashboard/backend/services/planoCanonico.js` — a fonte única. Este arquivo
 * tem só as funções de leitura, que são as mesmas do backend e cabem numa tela.
 *
 * Para mudar conta, atributo ou perfil: mexa no backend e rode
 * `cd dashboard/backend && npm run plano:sync`. O teste de deriva
 * (`backend/tests/planoCanonicoEspelho.test.js`) falha enquanto os dois
 * estiverem diferentes — foi para não repetir a armadilha dos dois leitores
 * espelhados de folha de conta que ele existe.
 */

import {
  NATUREZAS,
  COMPORTAMENTOS,
  PERFIS,
  PREFIXOS_LEGADOS,
  GRUPOS,
  CONTAS,
  TAGS,
  QUANDO
} from './planoCanonico.gerado';

export { NATUREZAS, COMPORTAMENTOS, PERFIS, PREFIXOS_LEGADOS, GRUPOS, CONTAS, TAGS, QUANDO };

const POR_CODIGO = Object.fromEntries(CONTAS.map((conta) => [conta.codigo, conta]));

/** A conta de um código canônico, ou `undefined`. */
export const contaPorCodigo = (codigo) => POR_CODIGO[String(codigo ?? '').trim()];

/** As contas de um perfil, na ordem do plano. `completo` é o plano inteiro. */
export const contasDoPerfil = (perfil) => (
  perfil === 'completo'
    ? CONTAS.slice()
    : CONTAS.filter((conta) => conta.perfis.includes(perfil))
);

/** O grupo (bloco de primeiro nível) a que uma conta pertence. */
export const grupoDaConta = (conta) => {
  const raiz = String(conta?.codigo ?? '').split('.')[0];
  return GRUPOS.find((grupo) => grupo.codigo === raiz) || null;
};

/**
 * As contas de um perfil agrupadas por bloco, na ordem da DRE — o formato que
 * o questionário e a planilha modelo consomem.
 */
export const blocosDoPerfil = (perfil) => {
  const contas = contasDoPerfil(perfil);
  return GRUPOS
    .map((grupo) => ({
      ...grupo,
      contas: contas.filter((conta) => String(conta.codigo).split('.')[0] === grupo.codigo)
    }))
    .filter((grupo) => grupo.contas.length > 0);
};

/**
 * O valor da coluna "Custo?" da planilha: `afetaResultado` na língua do
 * arquivo. É essa coluna que separa competência de caixa, e juros de principal.
 */
export const custoDaConta = (conta) => {
  // Receita nao tem "Custo?": a coluna sai em branco. Marcada "Sim", o
  // faturamento entrava no Custo Total Real do motor.
  if (!conta || conta.natureza === NATUREZAS.RECEITA) return '';
  return conta.afetaResultado ? 'Sim' : 'Não';
};

/** O código que o motor de indicadores de hoje entende, ou `null`. */
export const legadoDaConta = (conta) => conta?.legado ?? null;

/* ------------------------------------------------------------------------- *
 * O PERFIL A PARTIR DAS RESPOSTAS
 *
 * Espelha `backend/services/perfilDaEmpresa.js`. O DADO (TAGS e QUANDO) e
 * gerado; estas quatro funcoes sao a leitura, e existem aqui para a tela poder
 * dizer quantas linhas o arquivo vai ter ANTES de baixar. Mexeu na regra do
 * backend, mexa aqui — `perfilDaEmpresa.test.js` tranca o lado de la e
 * `planoCanonico.test.js` tranca este.
 * ------------------------------------------------------------------------- */

/** As tags de uma conta; ausente do mapa quer dizer "entra sempre". */
export const quandoDaConta = (codigo) => QUANDO[String(codigo ?? '').trim()] || [];

/**
 * Regime x "como o imposto aparece": as duas perguntas que convivem.
 * O regime manda quando conhecido; senao decide o que a pessoa ve acontecer.
 * Quando as duas discordam, devolve o conflito — a tela pergunta qual vale.
 */
export const resolverImposto = ({ regime = null, comoImposto = null } = {}) => {
  const r = regime === 'naoSei' ? null : regime;
  const c = comoImposto === 'naoSei' ? null : comoImposto;

  const discorda = (r === 'simples' && c === 'separados')
    || ((r === 'presumido' || r === 'real') && c === 'unico');

  const conflito = discorda
    ? {
      campo: 'imposto',
      pergunta: r === 'simples'
        ? 'Você marcou Simples Nacional, mas disse que paga ICMS e ISS em guias separadas. Uma das duas está trocada. Qual vale?'
        : 'Você marcou lucro presumido ou real, mas disse que paga uma guia só por mês. Uma das duas está trocada. Qual vale?'
    }
    : null;

  if (r === 'simples') return { tags: [TAGS.IMPOSTO_UNICO], conflito, aviso: null };
  if (r === 'presumido' || r === 'real') return { tags: [TAGS.IMPOSTO_SEPARADO], conflito, aviso: null };
  if (c === 'unico') return { tags: [TAGS.IMPOSTO_UNICO], conflito, aviso: null };
  if (c === 'separados') {
    return {
      tags: [TAGS.IMPOSTO_SEPARADO],
      conflito,
      aviso: 'Não sabemos o regime, então trouxemos as linhas de cada tributo e o IRPJ/CSLL. Confirme com seu contador.'
    };
  }
  return {
    tags: [TAGS.IMPOSTO_UNICO],
    conflito,
    aviso: 'Como não sabemos o regime nem como o imposto aparece, deixamos uma linha só de "impostos sobre a venda".'
  };
};

/** As respostas do questionario viram tags, avisos e conflitos. */
export const tagsDasRespostas = (respostas = {}) => {
  const tags = new Set();
  const avisos = [];
  const conflitos = [];
  const lista = (v) => (Array.isArray(v) ? v : []);

  lista(respostas.vende).forEach((v) => {
    if ([TAGS.REVENDE, TAGS.FABRICA, TAGS.SERVICO].includes(v)) tags.add(v);
  });
  lista(respostas.recebe).forEach((v) => {
    if ([TAGS.CARTAO, TAGS.PRAZO, TAGS.MARKETPLACE].includes(v)) tags.add(v);
  });
  if (respostas.temFuncionario === true) tags.add(TAGS.TEM_FUNCIONARIO);
  if (respostas.temDivida === true) tags.add(TAGS.TEM_DIVIDA);

  const imposto = resolverImposto(respostas);
  imposto.tags.forEach((t) => tags.add(t));
  if (imposto.conflito) conflitos.push(imposto.conflito);
  if (imposto.aviso) avisos.push(imposto.aviso);

  if (respostas.profundidade === 'conta') tags.add(TAGS.DETALHADO);

  return { tags: Array.from(tags), conflitos, avisos };
};

/** As contas que essa empresa tem, na ordem do plano. */
export const contasParaRespostas = (respostas = {}) => {
  const marcadas = new Set(tagsDasRespostas(respostas).tags);
  return CONTAS.filter((conta) => {
    const quando = quandoDaConta(conta.codigo);
    return quando.length === 0 || quando.some((t) => marcadas.has(t));
  });
};
